import layoutStyles from '@ds/foundations/layout.module.css';
import { type LayoutElementProps, type LayoutProps, resolveLayoutProps } from '@ds/foundations/layoutProps';
import paddingStyles from '@ds/foundations/padding.module.css';
import { type BlockPaddingProps, resolvePaddingProps } from '@ds/foundations/paddingProps';
import { Slot } from '@ds/foundations/Slot';
import clsx from 'clsx';
import React, { forwardRef, type ReactNode } from 'react';

import styles from './Container.module.css';

/**
 * A layout primitive that centres page content within a fixed max-width.
 *
 * `Container` handles the outer shell of a page layout: a centred 1128px content column
 * (`--container-content-width`) with side padding of 16px on mobile and 32px from 600px
 * (`--container-padding`). Page chrome that reads the same tokens (a header and footer) shares
 * one left and right edge with it.
 *
 * It carries no visual appearance — no background, border, or shadow.
 * Use it as the outermost wrapper for any content that should be page-width constrained.
 *
 * Accepts the shared **layout props** (`position`, offsets, size, `overflow`, flex-item props — see
 * `layoutProps.ts`), like every layout primitive, and the **block-axis padding props** (`py`, `pt`,
 * `pb` — see `paddingProps.ts`). There is no inline padding prop: the left and right padding is
 * `--container-padding`, which the page chrome shares.
 *
 * Typically paired with `Grid` for column-based layouts:
 *
 * @example
 * <Container>
 *     <Grid>
 *         <Grid.Col span={{ desktop: 6 }}>...</Grid.Col>
 *         <Grid.Col span={{ desktop: 6 }}>...</Grid.Col>
 *     </Grid>
 * </Container>
 *
 * @example
 * // Semantic main element — same centring behaviour, correct HTML landmark
 * <Container as="main">
 *     <PageContent />
 * </Container>
 *
 * @example
 * // Space above and below the page column — the side padding is unchanged
 * <Container as="main" py={8}>
 *     <PageContent />
 * </Container>
 */
export interface ContainerProps
    extends React.HTMLAttributes<HTMLElement>, LayoutElementProps, BlockPaddingProps, LayoutProps {
    children?: ReactNode;
}

const Container = forwardRef<HTMLElement, ContainerProps>(
    ({ as: Tag = 'div', asChild, className, style, children, ...others }, ref) => {
        // Block padding props split off first, then the layout props — gated data-* attributes +
        // custom properties read by the shared padding.module.css and layout.module.css. Block
        // padding is its own gate, so it never touches the inline --container-padding.
        const padding = resolvePaddingProps(others);
        const { attrs, vars, rest } = resolveLayoutProps(padding.rest);

        // asChild renders the one child element instead, with these props merged onto it (Slot).
        const Comp: React.ElementType = asChild ? Slot : Tag;

        return (
            <Comp
                ref={ref}
                {...padding.attrs}
                {...attrs}
                className={clsx(styles.container, paddingStyles.padding, layoutStyles.layout, className)}
                style={{ ...padding.vars, ...vars, ...style }}
                {...rest}
            >
                {children}
            </Comp>
        );
    }
);

Container.displayName = 'Container';

export { Container };
export default Container;
